import { getItem, setItem, useAsyncMemo } from "@buzzeasy/shared-frontend-utilities";
import { PropsWithChildren, ReactElement, useMemo } from "react";
import environmentConfig from "../../../environmentConfig";
import FullScreenSpinner from "../../presenters/FullScreenSpinner";
import { HostThemeConfig, HostThemeConfigContext } from "./HostThemeConfigContext";

const { environment, themeRepoUrl } = environmentConfig;

export default function HostThemeConfigProvider({ children }: PropsWithChildren): ReactElement {
  const ignoreThemeConfig = useMemo(
    () => !!getItem("aui-ignoreThemeConfig"),
    [],
  );

  const configOverride = useMemo(
    () => {
      if (environment === "prod")
        return null;

      return getItem<HostThemeConfig>("aui-themeConfigOverride");
    },
    [],
  );

  const savedConfig = useMemo(
    () => getItem<HostThemeConfig>("aui-cachedThemeConfig"),
    [],
  );

  const hostThemeConfig = useAsyncMemo(
    async () => {
      try {
        const resp = await fetch(`${themeRepoUrl}/${location.hostname}.json`);
        const config = resp.ok ? await resp.json() as HostThemeConfig : null;

        if (config)
          setItem("aui-cachedThemeConfig", config);

        return config;
      }
      catch {
        return null;
      }
    },
    [],
  );

  const usedConfig = hostThemeConfig ?? savedConfig;

  const useConfigWithOverrides = useMemo(
    () => usedConfig ? { ...usedConfig, ...configOverride } : null,
    [configOverride, usedConfig],
  );

  // hostThemeConfig is undefined when the fetch is still pending
  if (hostThemeConfig === undefined && savedConfig === null)
    return <FullScreenSpinner />;

  return (
    <HostThemeConfigContext.Provider value={ignoreThemeConfig ? null : useConfigWithOverrides}>
      {children}
    </HostThemeConfigContext.Provider>
  );
}
